import React, { useEffect, useState, useMemo, useRef } from "react";
import MapGL, { Source, Layer, Marker } from "react-map-gl";

import { LocationDetailsSidebar } from "./LocationDetailsSidebar";
import { MapStyleSelectorTool } from "../map/MapStyleSelectorHelper";
import Pin from "../map/pin";
import { RiskIndexesLayerId, riskLayersEnum } from "./ClimateIndexConstants";

import { getZippedGeoJson } from "../map/ZippedGeoJsonReader";
import { MapLayersSelector } from "../map/MapLayerSelector";
import { lightMapStye, defaultRomeLatitude, defaultRomeLongitude } from "../map/Constants";
import { ScaleBar } from "../map/ScaleBar";
import { saveUserMonitorData } from "../util/userUtils";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const defaultMapBoxStyleUrl = lightMapStye;
const defaultMapStyle = "mapbox/light-v10";

export const ClimateIndexMap = () => {
  const mapRef = useRef();

  const [viewState, setViewState] = useState({
    latitude: defaultRomeLatitude,
    longitude: defaultRomeLongitude,
    zoom: 6,
  });
  const [mapStyle, setMapStyle] = useState(defaultMapBoxStyleUrl);
  const [mapSelect, setMapSelect] = useState(defaultMapStyle);
  const [geoJson, setGeoJson] = useState("");
  const [selectedProperties, setSelectedProperties] = useState(null);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [currentClimateIndex, setCurrentClimateIndex] = useState(riskLayersEnum().cystellarNaturalEvents.id);

  const validateFeaturePropertyValue = (data, propertyName) => {
    // The map rendering says "Expected value to be of type number, but found null instead."
    // The reason is somewhere in the library, data is OK.
    data.features.forEach((feature) => {
      if (feature.properties[propertyName] === undefined) {
        console.error("==Missing value for ", propertyName, ":", feature.properties);
      }

      if (isNaN(feature.properties[propertyName])) {
        console.error("==Value is not numeric for ", propertyName, ":", feature.properties);
      }
    });
  };

  useEffect(() => {
    saveUserMonitorData("climateIndexMap", `${currentClimateIndex}`);

    const currentLayerGeojsonUrlZipped = riskLayersEnum()[currentClimateIndex].geojsonUrl;
    getZippedGeoJson(currentLayerGeojsonUrlZipped).then((data) => {
      validateFeaturePropertyValue(data, riskLayersEnum()[currentClimateIndex].propertyName);
      setGeoJson(data);
    });
  }, [currentClimateIndex]);

  const data = useMemo(() => {
    return geoJson;
  }, [geoJson]);

  const onMove = (viewState) => {
    setViewState(viewState);
  };

  const onClick = (event) => {
    setLongitude(event.lngLat.lng);
    setLatitude(event.lngLat.lat);
    const { features } = event;

    const selectedFeature = features && features[0];
    if (selectedFeature) {
      setSelectedProperties(selectedFeature.properties);
    } else {
      setSelectedProperties(null);
    }
  };

  return (
    <MapGL
      {...viewState}
      ref={mapRef}
      mapboxAccessToken={TOKEN}
      onMove={(evt) => onMove(evt.viewState)}
      interactiveLayerIds={[RiskIndexesLayerId]}
      mapStyle={mapStyle}
      onClick={onClick}
    >
      {data && (
        <Source type="geojson" data={data}>
          <Layer {...riskLayersEnum()[currentClimateIndex].mapLayer} />
        </Source>
      )}

      {riskLayersEnum()[currentClimateIndex].legend}

      {MapStyleSelectorTool(mapSelect, setMapSelect, setMapStyle, defaultMapStyle)}

      <MapLayersSelector
        currentIndex={currentClimateIndex}
        setCurrentIndex={setCurrentClimateIndex}
        layersList={riskLayersEnum()}
      />

      <LocationDetailsSidebar selectedProperties={selectedProperties} climateIndex={currentClimateIndex} />

      {/* in order to hide the pin outside of the Italy map add the selectedProperties check */}
      {latitude && longitude && (
        <Marker longitude={longitude} latitude={latitude}>
          <Pin size={20} />
        </Marker>
      )}
      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};
